import { useCallback, useEffect, useState } from "react";
import { loadMe } from "../apiClient.js";
import { isAuthEnabled } from "./supabaseClient.js";
import { safeLocalStorageGet, safeLocalStorageSet } from "./storage.js";

interface MeState {
  isAdmin: boolean;
  /** Permanent handle: the address of the user's own author page. */
  username: string;
  displayName: string;
  avatarUrl: string | null;
  /** When the account's profile was created (ISO), or null when unknown. */
  createdAt: string | null;
  /**
   * False while the account still has to pick its username (a first Google /
   * Discord sign-in): the app shows "Finish signing up". True until known.
   */
  usernameConfirmed: boolean;
  /** True once this user's profile is known (from /api/me, or remembered from it). */
  loaded: boolean;
  /** Whose profile this is, so a sign-in switch never shows the previous user's. */
  userId: string | null;
}

const SIGNED_OUT: MeState = {
  isAdmin: false,
  username: "",
  displayName: "",
  avatarUrl: null,
  createdAt: null,
  usernameConfirmed: true,
  loaded: false,
  userId: null
};

const meCacheKey = (userId: string) => `lorevok:me:${userId}`;

/**
 * The last /api/me answer for this user, so the topbar can show the author's
 * real name and avatar immediately on a return visit instead of a placeholder
 * (or, formerly, the English "Author" / the email) while the API answers.
 */
function cachedMe(userId: string | null | undefined): MeState {
  if (!isAuthEnabled || !userId) return SIGNED_OUT;
  try {
    const raw = safeLocalStorageGet(meCacheKey(userId));
    if (!raw) return { ...SIGNED_OUT, userId };
    const parsed = JSON.parse(raw) as Partial<MeState>;
    return {
      isAdmin: parsed.isAdmin === true,
      username: typeof parsed.username === "string" ? parsed.username : "",
      displayName: typeof parsed.displayName === "string" ? parsed.displayName : "",
      avatarUrl: typeof parsed.avatarUrl === "string" ? parsed.avatarUrl : null,
      createdAt: typeof parsed.createdAt === "string" ? parsed.createdAt : null,
      // Remembered so a returning account that has not finished signing up is
      // held on that screen at once (entries from before the flag: confirmed).
      usernameConfirmed: parsed.usernameConfirmed !== false,
      loaded: true,
      userId
    };
  } catch {
    return { ...SIGNED_OUT, userId };
  }
}

/**
 * Fetches the signed-in user's admin flag and public profile (username, display
 * name, avatar) from /api/me. Neutral defaults when auth is off or signed out.
 * Re-fetched when the user changes, and on demand via `refresh` (e.g. after the
 * author edits their profile) so the topbar identity updates without a reload.
 * `loaded` stays false until the profile is known, so callers can show a
 * neutral placeholder rather than a wrong name or a wrong admin decision.
 */
export function useMe(userId: string | null | undefined): {
  isAdmin: boolean;
  username: string;
  displayName: string;
  avatarUrl: string | null;
  createdAt: string | null;
  usernameConfirmed: boolean;
  loaded: boolean;
  refresh: () => void;
} {
  const [me, setMe] = useState<MeState>(() => cachedMe(userId));

  const fetchMe = useCallback((): (() => void) => {
    if (!isAuthEnabled || !userId) {
      setMe(SIGNED_OUT);
      return () => undefined;
    }
    let active = true;
    // A different user (sign-in switch): start from what we remember of them.
    // A refresh for the same user keeps the current profile until the answer.
    setMe((current) => (current.userId === userId ? current : cachedMe(userId)));
    void loadMe()
      .then((result) => {
        if (!active) return;
        const next: MeState = {
          isAdmin: result.isAdmin,
          username: result.username,
          displayName: result.displayName,
          avatarUrl: result.avatarUrl,
          createdAt: result.createdAt,
          usernameConfirmed: result.usernameConfirmed !== false,
          loaded: true,
          userId
        };
        setMe(next);
        safeLocalStorageSet(
          meCacheKey(userId),
          JSON.stringify({
            isAdmin: next.isAdmin,
            username: next.username,
            displayName: next.displayName,
            avatarUrl: next.avatarUrl,
            createdAt: next.createdAt,
            usernameConfirmed: next.usernameConfirmed
          })
        );
      })
      .catch(() => {
        // Keep a remembered profile on a failed refresh; otherwise settle on the
        // neutral signed-in state so nothing waits forever.
        if (active) setMe((current) => (current.loaded ? current : { ...SIGNED_OUT, loaded: true, userId }));
      });
    return () => {
      active = false;
    };
  }, [userId]);

  useEffect(() => fetchMe(), [fetchMe]);

  return {
    isAdmin: me.isAdmin,
    username: me.username,
    displayName: me.displayName,
    avatarUrl: me.avatarUrl,
    createdAt: me.createdAt,
    usernameConfirmed: me.usernameConfirmed,
    loaded: me.loaded,
    refresh: () => fetchMe()
  };
}
